import { coordinatesState } from '../../../utils/coordinates';
import {
    type StationCreationForm,
    bookableObjectCount,
    parseCount,
    resolvedProductRows,
} from './stationCreationForm';

/** Safety ceiling on positions — guards the per-object render (manual naming
 *  builds one input per bookable object) against a runaway count. */
export const MAX_POSITIONS = 500;

/**
 * Pure, per-step validation for the station-creation wizard. Returns the first
 * failing i18n key for a step, or null when the step is valid. Kept as a pure
 * function (no React) so it is unit-testable without mounting the wizard — the
 * established admin-ui pattern for step/validation logic.
 *
 * Step indices: 0 Location · 1 Station · 2 Objects · 3 Link to Product.
 */
/** Step 0 — the Location the station is placed at. */
const locationStepError = (form: StationCreationForm): string | null => {
    if (form.locationMode === 'existing') {
        return form.existingLocationId
            ? null
            : 'station_create.validation.location_required';
    }
    if (!form.newLocationName.trim()) {
        return 'station_create.validation.new_location_name_required';
    }
    // The address is not decoration. It is what the end-user app shows in the
    // location picker, and a location created without one renders as
    // "undefined, " in the admin and threw in the app.
    if (!form.newLocationStreet.trim()
        || !form.newLocationPostalCode.trim()
        || !form.newLocationCity.trim()) {
        return 'station_create.validation.address_required';
    }
    // Optional, but a half pin is no place.
    if (coordinatesState({ geoLat: form.newLocationGeoLat, geoLng: form.newLocationGeoLng }) === 'incomplete') {
        return 'coordinates.incomplete';
    }
    // A weekday with no row is CLOSED, so an all-empty week would create a
    // station nothing can ever be booked at.
    if (form.newLocationOpeningHours.length === 0) {
        return 'station_create.validation.opening_hours_required';
    }
    // A half-typed row is dropped rather than reported by the editor, so
    // "valid" has to come from the editor itself — otherwise the wizard
    // silently creates a station closed on the day being typed.
    if (!form.newLocationOpeningHoursValid) {
        return 'station_create.validation.opening_hours_invalid';
    }
    return null;
};

/** Step 1 — the Station itself (= depot): name, positions, held-back positions. */
const stationDetailsStepError = (form: StationCreationForm): string | null => {
    // Only when the station sits at an EXISTING location does it need its own
    // name — a new location was named on step 0 and IS this station.
    if (form.locationMode === 'existing' && !form.stationName.trim()) {
        return 'station_create.validation.station_name_required';
    }
    // A room-station is one object (itself) — no positions/buffer.
    if (form.stationType === 'room') {
        return null;
    }
    const positions = parseCount(form.positionCount);
    if (positions === null || positions < 1) {
        return 'station_create.validation.position_count_invalid';
    }
    if (positions > MAX_POSITIONS) {
        return 'station_create.validation.position_count_too_large';
    }
    const buffer = parseCount(form.bufferCount);
    if (buffer === null) {
        return 'station_create.validation.buffer_invalid';
    }
    if (buffer >= positions) {
        return 'station_create.validation.buffer_too_large';
    }
    return null;
};

/** Step 2 — the objects that occupy the station's positions. */
const objectsStepError = (form: StationCreationForm): string | null => {
    const count = bookableObjectCount(form);
    if (count === null || count < 1) {
        return 'station_create.validation.position_count_invalid';
    }
    if (form.objectNaming === 'auto') {
        return form.objectNamePrefix.trim()
            ? null
            : 'station_create.validation.name_prefix_required';
    }
    // manual: exactly `count` non-empty names
    const filled = form.objectNames.slice(0, count).filter((n) => n.trim());
    return filled.length === count
        ? null
        : 'station_create.validation.object_names_incomplete';
};

/**
 * Step 3 — the Products the objects fulfil. Required, now that Products exist
 * (#1712) and the picker is wired: objects created without one are configured
 * and unbookable — the exact outcome the interim `Group` was introduced to
 * prevent — so this blocks rather than warns.
 *
 * With several Products at one station (#2280) the same rule applies per row,
 * plus the split has to account for every position: a row list that does not
 * total the bookable objects would leave positions empty or overflow the
 * cabinet, and both are silent.
 */
const productStepError = (form: StationCreationForm): string | null => {
    const rows = form.productRows;
    if (rows.length === 0 || rows.some((row) => !row.productId.trim())) {
        return 'station_create.validation.product_required';
    }
    // The same Product twice is not an error the backend would reject — it
    // creates one cell and links both blocks to it — but it is never what the
    // operator meant, and it silently halves nothing while looking like a
    // split. Two rows of "Fach L" is a typo, so say so.
    const ids = rows.map((row) => row.productId.trim());
    if (new Set(ids).size !== ids.length) {
        return 'station_create.validation.product_duplicate';
    }
    // One row means "all of them", so there is no count to check.
    if (rows.length === 1) return null;
    if (rows.some((row) => {
        const count = parseCount(row.count);
        return count === null || count < 1;
    })) {
        return 'station_create.validation.product_count_invalid';
    }
    return resolvedProductRows(form) === null
        ? 'station_create.validation.product_counts_mismatch'
        : null;
};

/** The house variant's step 1: a name when it stands at an existing location. */
const houseStepDetailsError = (form: StationCreationForm): string | null => (
    form.locationMode === 'existing' && !form.stationName.trim()
        ? 'house_create.validation.house_name_required'
        : null
);

const HOUSE_STEP_VALIDATORS: Array<(form: StationCreationForm) => string | null> = [
    locationStepError,
    houseStepDetailsError,
];

/** The house variant's per-step validation: 0 Location · 1 House. */
export const houseStepError = (
    step: number,
    form: StationCreationForm,
): string | null => HOUSE_STEP_VALIDATORS[step]?.(form) ?? null;

const STEP_VALIDATORS: Array<(form: StationCreationForm) => string | null> = [
    locationStepError,
    stationDetailsStepError,
    objectsStepError,
    productStepError,
];

export const stationStepError = (
    step: number,
    form: StationCreationForm,
): string | null => STEP_VALIDATORS[step]?.(form) ?? null;

export const isStationStepValid = (step: number, form: StationCreationForm): boolean =>
    stationStepError(step, form) === null;
